import {useMutation} from '@tanstack/react-query';

import {TypeLocalNameEnum} from 'domain/usecases';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {postOdontoProviders} from '../../services/postOdontoProviders';
import {useQueryParams} from 'presentation/pages';
import {DisabilityEnum, NearNeighborhoodEnum} from 'presentation/constants';
import {
	MAX_DISTRICT_FILTER_COUNT,
	MIN_DISTANCE_IN_KM,
	MIN_DISTANCE_TO_FETCH_ODONTO_UNITS,
} from 'presentation/modules/booking/shared/constants/unitsInfo';
import {parseOdontoDistrictFilter} from 'presentation/modules/booking/odonto/utils/parseOdontoDistrictFilter';

export function usePostOdontoProvidersMutation() {
	const bookingData = useBookingStore(state => state.bookingData);
	const {
		SEARCH_PARAMS: {disability, includeNeighborhoodDistricts, localityDistance},
	} = useQueryParams();

	const {
		specialtyCode,
		subSpecialtyCode,
		ufCode,
		isManualLocation,
		cityName,
		hasGeoDistrictsFilter,
		coordinates,
		allDistrictsSelected,
		districtName,
		citiesNames,
	} = bookingData;

	const distanceFilterAtMinimumDistance = localityDistance === MIN_DISTANCE_IN_KM;
	const isUsingGeolocation = !isManualLocation;
	const isUsingGeoAndHasDistrictsFilter = isUsingGeolocation && hasGeoDistrictsFilter;
	const isUsingGeoAndHasNoDistrictsFilter = isUsingGeolocation && !hasGeoDistrictsFilter;

	const districtFilter = parseOdontoDistrictFilter({
		districtName,
		citiesNames,
		stateCode: ufCode,
	});

	const shouldSendEmptyDistrictFilter =
		districtFilter?.length > MAX_DISTRICT_FILTER_COUNT || allDistrictsSelected;

	const getDistanceValue = () => {
		if (!isUsingGeolocation || isUsingGeoAndHasDistrictsFilter) {
			return undefined;
		}

		if (distanceFilterAtMinimumDistance || !localityDistance) {
			return MIN_DISTANCE_TO_FETCH_ODONTO_UNITS;
		}

		return localityDistance;
	};

	const params = {
		cdUf: isUsingGeoAndHasNoDistrictsFilter ? undefined : ufCode?.toUpperCase(),
		nmCidade: isUsingGeoAndHasNoDistrictsFilter ? undefined : cityName?.toUpperCase(),
		cdEspecialidade: specialtyCode,
		cdAtendimentoAcessoEspecial: (disability as DisabilityEnum) || DisabilityEnum.ALL,
		cdSubEspecialidade: subSpecialtyCode,
		tpLocalidade: isUsingGeoAndHasNoDistrictsFilter
			? TypeLocalNameEnum.COORDENADAS
			: TypeLocalNameEnum.CIDADE,
		incluirBairrosVizinhos:
			(includeNeighborhoodDistricts as NearNeighborhoodEnum) ||
			NearNeighborhoodEnum.NOT_INCLUDE,
		filtroBairro: shouldSendEmptyDistrictFilter ? [] : districtFilter,
		nmLocal: isUsingGeoAndHasNoDistrictsFilter ? coordinates : undefined,
		distancia: getDistanceValue(),
	};

	return useMutation({
		mutationFn: async ({signal}: {signal?: AbortSignal}) =>
			postOdontoProviders({...params, signal}),
	});
}
